import { useState } from "react";
import { Badge, Button, Card, Group, Skeleton, Stack, Table, Text, Textarea } from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { flattenMealResult, humanizedToShoppingItems, shoppingListText, type ShoppingItem } from "@passio/api-client";
import { passio } from "../../lib/clients";
import { composeMessage } from "../../lib/clientSettings";
import { useSession } from "../../lib/session";
import { ErrorNotice } from "../../components/common";
import { MealGroupsView } from "../../components/food";
import type { ShoppingHandoff } from "../Shopping";
import { foodsToShoppingItems, RunBar, Step, TourTimeline, type TourFood } from "./shared";

const RECIPES = [
  "Weeknight chicken stir fry for 4: 500 g chicken breast, 2 red bell peppers, 1 head of broccoli, 3 tbsp soy sauce, 1 tbsp sesame oil, 2 cloves garlic and 2 cups of jasmine rice",
  "Overnight oats: 1/2 cup rolled oats, 3/4 cup almond milk, 1 tbsp chia seeds, 1/2 cup blueberries and a drizzle of honey",
  "Taco night: 1 lb ground beef, taco seasoning, 8 corn tortillas, shredded lettuce, 1 cup cheddar, salsa and sour cream",
];

/** Recipe text to a structured meal, then shopper-friendly quantities, then a cart. */
export function RecipeTour({ onSelect }: { onSelect: (f: TourFood) => void }) {
  const { hasFeature } = useSession();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(RECIPES[0]);
  const structure = useMutation({ mutationFn: (t: string) => passio.tools.extractFoodAndIngredients(t, composeMessage("")) });
  const humanize = useMutation({
    mutationFn: (v: { items: ShoppingItem[]; recipe: string }) => passio.tools.humanizeShoppingList(shoppingListText(v.items), `Amounts come from this recipe: ${v.recipe}`),
  });
  const cart = useMutation({ mutationFn: (items: ShoppingItem[]) => passio.affiliates.convertShoppingList(items, { title: "Passio recipe tour", campaign: "passio-demo-tour", sourceTool: "passio-demo" }) });
  const [running, setRunning] = useState(false);

  const can = { text: hasFeature("text_ai"), shop: hasFeature("shopping") };
  const items = flattenMealResult(structure.data?.data);
  const raw = humanize.variables?.items ?? [];
  const human = humanize.data?.data.items ?? [];
  const cartList = humanizedToShoppingItems(humanize.data?.data);

  const reset = () => [structure, humanize, cart].forEach((m) => m.reset());

  const runAll = async () => {
    reset();
    setRunning(true);
    try {
      const meal = flattenMealResult((await structure.mutateAsync(recipe)).data);
      if (can.shop && meal.length) {
        const lines = foodsToShoppingItems(meal);
        const list = humanizedToShoppingItems((await humanize.mutateAsync({ items: lines, recipe })).data);
        await cart.mutateAsync(list.length ? list : lines);
      }
    } catch {
      // Each step shows its own error.
    } finally {
      setRunning(false);
    }
  };

  const busy = running || structure.isPending;
  const active = cart.data ? 3 : humanize.data ? 2 : structure.data ? 1 : 0;
  const locked = can.shop ? undefined : "Shopping isn't enabled for this account.";

  if (!can.text) {
    return (
      <Card>
        <Text c="dimmed">Text AI tools aren't enabled for this account, so the recipe tour can't run.</Text>
      </Card>
    );
  }

  return (
    <Stack gap="lg">
      <Card p={{ base: "md", sm: "xl" }}>
        <Stack gap="sm">
          <Textarea label="Paste a recipe or ingredient list" autosize minRows={3} size="md" value={recipe} onChange={(e) => setRecipe(e.currentTarget.value)} disabled={busy} />
          <Group gap={6}>
            {RECIPES.map((r) => (
              <Badge key={r} variant="outline" color="gray" tt="none" style={{ cursor: "pointer" }} onClick={() => !busy && setRecipe(r)}>
                {r.split(":")[0]}
              </Badge>
            ))}
          </Group>
          <RunBar busy={busy} canRun={Boolean(recipe.trim())} hasResults={active > 0} onRun={runAll} onClear={reset} />
        </Stack>
      </Card>

      <TourTimeline
        active={active}
        steps={[
          {
            label: "Structure the recipe",
            body: (
              <Step title="Text to a structured meal" route="POST /napi/tools/extractFoodAndIngredients" to="/text" done={Boolean(structure.data)}>
                {structure.error ? <ErrorNotice error={structure.error} /> : null}
                {structure.isPending && <Skeleton h={140} radius="md" />}
                {structure.data && !structure.isPending && <MealGroupsView items={items} onSelect={onSelect} fallbackMealName="Your recipe" />}
                {!structure.data && !structure.isPending && !structure.error && (
                  <Text fz="sm" c="dimmed">
                    One meal back, split into components with each ingredient matched to a Passio food, so you get nutrition per component and for the whole recipe.
                  </Text>
                )}
              </Step>
            ),
          },
          {
            label: "Make it shoppable",
            body: (
              <Step title="Shopper-friendly quantities" route="POST /napi/tools/humanizeShoppingList" to="/shopping" done={Boolean(humanize.data)} locked={locked}>
                {humanize.error ? <ErrorNotice error={humanize.error} /> : null}
                {humanize.isPending && <Skeleton h={120} radius="md" />}
                {humanize.data && !humanize.isPending && (
                  <Table verticalSpacing={4} fz="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>From the recipe</Table.Th>
                        <Table.Th>What to buy</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {human.map((h, i) => {
                        const from = raw.find((r) => r.name.toLowerCase() === (h.name ?? "").toLowerCase()) ?? raw[i];
                        return (
                          <Table.Tr key={i}>
                            <Table.Td c="dimmed">{from ? [from.portionQuantity, from.portionSize, from.name].filter(Boolean).join(" ") : h.name}</Table.Td>
                            <Table.Td>
                              <Text fz="sm" fw={600}>
                                {[h.quantity, h.unit, h.shoppingName || h.name].filter(Boolean).join(" ")}
                              </Text>
                              {h.notes && (
                                <Text fz="xs" c="dimmed">
                                  {h.notes}
                                </Text>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                )}
                {!humanize.data && !humanize.isPending && !humanize.error && (
                  <Text fz="sm" c="dimmed">
                    “2 cups of jasmine rice” isn't something you can buy. This rewrites recipe amounts into store units: a bag of rice, a bottle of soy sauce, a head of garlic.
                  </Text>
                )}
              </Step>
            ),
          },
          {
            label: "Cart",
            body: (
              <Step title="Instacart link" route="POST /napi/affiliates/convertShoppingListToAffiliateLink" to="/shopping" done={Boolean(cart.data)} locked={locked}>
                {cart.error ? <ErrorNotice error={cart.error} /> : null}
                {cart.isPending && <Skeleton h={50} radius="md" />}
                {cart.data && !cart.isPending && (
                  <Group gap="xs">
                    <Button component="a" href={cart.data.data.passio_redirect_url} target="_blank" rel="noreferrer" rightSection={<IconExternalLink size={16} />}>
                      Open the {(cart.data.data.affiliate ?? "instacart").replace(/^\w/, (c) => c.toUpperCase())} cart
                    </Button>
                    <Button variant="default" onClick={() => navigate("/shopping", { state: { items: cartList, title: "Passio recipe tour" } satisfies ShoppingHandoff })}>
                      Edit the list
                    </Button>
                  </Group>
                )}
                {!cart.data && !cart.isPending && !cart.error && (
                  <Text fz="sm" c="dimmed">
                    The shopper-friendly list becomes a tracked Instacart link with first-party click attribution.
                  </Text>
                )}
              </Step>
            ),
          },
        ]}
      />
    </Stack>
  );
}
